import { IndicatorType } from '../constants/common'
import utils from '../utils'
import { getFirstBarToRenderIndex } from '../utils/draw_in_visible_area'
import Indicator from './indicator'

interface Attrs {
  period: string
}

class ADX extends Indicator<Attrs> {
  static type = IndicatorType.Adx
  static label = 'ADX'

  declare period: number
  pdi: number[] = []
  mdi: number[] = []
  adx: number[] = []

  set(values: Partial<Attrs>) {
    super.set(values)
    this.parsePeriodInt(values)
  }

  compute() {
    if (this.isComputeNecessary()) {
      let adx, amdm, apdm, mdi, pdi
      this.pdi = []
      this.mdi = []
      this.adx = []
      let atr = (apdm = amdm = adx = 0)
      for (let i = this.period; i < this.data.close.length; i++) {
        const deltaHigh = this.data.high[i] - this.data.high[i - 1]
        const deltaLow = this.data.low[i - 1] - this.data.low[i]

        let pdm = 0
        if (deltaHigh > 0 && deltaHigh > deltaLow) {
          pdm = deltaHigh
        }

        let mdm = 0
        if (deltaLow > 0 && deltaLow > deltaHigh) {
          mdm = deltaLow
        }

        const tr = utils.max([
          this.data.high[i] - this.data.low[i],
          Math.abs(this.data.high[i] - this.data.close[i - 1]),
          Math.abs(this.data.low[i] - this.data.close[i - 1]),
        ])

        atr = (atr * (this.period - 1) + tr) / this.period
        apdm = (apdm * (this.period - 1) + pdm) / this.period
        amdm = (amdm * (this.period - 1) + mdm) / this.period

        pdi = atr > 0 ? (apdm * 100) / atr : 0
        mdi = atr > 0 ? (amdm * 100) / atr : 0
        const di = Math.abs(pdi - mdi)
        const dx = pdi + mdi > 0 ? (di * 100) / (pdi + mdi) : 0
        adx = (adx * (this.period - 1) + dx) / this.period

        this.pdi[i] = pdi
        this.mdi[i] = mdi
        this.adx[i] = adx
      }
      this.lastValue = this.adx.last() ?? null
    }
    const { min, max } =
      this.pdi.length > 0 || this.mdi.length > 0 || this.adx.length > 0
        ? this.computeVisibleMinMax(this.pdi, this.mdi, this.adx)
        : this.getDomainDefaults(this.type)
    this.min = min
    this.max = max
  }

  renderIndicator(context: CanvasRenderingContext2D) {
    const firstBarToRender = getFirstBarToRenderIndex(this.data.close, this.period, this.fx, this.leftOffset)
    if (firstBarToRender === undefined) return
    let lastBarToRender = 0

    context.translate(0.5, 0.5)
    context.set('strokeStyle', '#2eb12e')
    context.beginPath()
    for (let i = firstBarToRender; i < this.data.close.length; i++) {
      const x = this.fx(i)
      if (x + this.leftOffset > this.width + 10) {
        break
      }
      context.lineTo(x, Math.round(this.fy(this.pdi[i])))
      lastBarToRender = i
    }
    context.stroke()

    context.set('strokeStyle', '#d43737')
    context.beginPath()
    for (let i = firstBarToRender; i <= lastBarToRender; i++) {
      context.lineTo(this.fx(i), Math.round(this.fy(this.mdi[i])))
    }
    context.stroke()
    context.set('strokeStyle', this.getChartLayoutSettings().IndicatorSettings.general.Colors.line)
    context.beginPath()
    for (let i = firstBarToRender; i <= lastBarToRender; i++) {
      context.lineTo(this.fx(i), Math.round(this.fy(this.adx[i])))
    }
    context.stroke()
    context.translate(-0.5, -0.5)
  }

  getModalConfig() {
    const period = {
      type: 'number',
      label: 'Period',
      name: 'period',
      value: this.period ?? 14,
      required: true,
      min: 1,
      max: 999999,
    }

    return {
      title: this.label,
      inputs: [period],
      inputsErrorMessages: {
        period: `${period.label} must be a whole number between ${period.min} and ${period.max}`,
      },
    }
  }

  getIsValid(key: string): boolean {
    switch (key) {
      case 'period':
        return this.getIsNumberInputValid({ key })
      default:
        return false
    }
  }

  toString() {
    return `ADX ${this.period}`
  }
}

export default ADX
